import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient, TicketCreateRequest, TicketUpdateRequest } from "@/lib/api";
import { getErrorMessage } from "@/lib/utils";
import { toast } from "sonner";

export function useTickets(filters?: Record<string, any>) {
  const queryClient = useQueryClient();

  const ticketsQuery = useQuery({
    queryKey: ["tickets", filters],
    queryFn: async () => {
      const response = await apiClient.getTickets(filters);
      return response;
    },
  });

  const stagesQuery = useQuery({
    queryKey: ["stages"],
    queryFn: async () => {
      const stages = await apiClient.getStages();
      return stages;
    },
  });

  const updateTicketStage = useMutation({
    mutationFn: async ({
      ticketId,
      stageId,
    }: {
      ticketId: string;
      stageId: string;
    }) => {
      return await apiClient.updateTicketStage(ticketId, stageId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      toast.success("Ticket stage updated");
    },
    onError: (error) => {
      toast.error("Failed to update ticket stage: " + error.message);
    },
  });

  const updateTicket = useMutation({
    mutationFn: async ({
      ticketId,
      updates,
    }: {
      ticketId: string;
      updates: TicketUpdateRequest;
    }) => {
      return await apiClient.updateTicket(ticketId, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      toast.success("Ticket updated");
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to update ticket"));
    },
  });

  const assignTicket = useMutation({
    mutationFn: async ({
      ticketId,
      userIds,
    }: {
      ticketId: string;
      userIds: string[];
    }) => {
      return await apiClient.assignTicket(ticketId, userIds);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      toast.success("Ticket assigned");
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to assign ticket"));
    },
  });

  const deleteTicket = useMutation({
    mutationFn: async (ticketId: string) => {
      return await apiClient.deleteTicket(ticketId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      toast.success("Ticket deleted");
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to delete ticket"));
    },
  });

  return {
    tickets: ticketsQuery.data?.data ?? [],
    pagination: ticketsQuery.data?.pagination,
    stages: stagesQuery.data ?? [],
    isLoading: ticketsQuery.isLoading || stagesQuery.isLoading,
    error: ticketsQuery.error || stagesQuery.error,
    updateTicketStage,
    updateTicket,
    assignTicket,
    deleteTicket,
    refetch: () => {
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      queryClient.invalidateQueries({ queryKey: ["stages"] });
    },
  };
}

export function useTicketById(id: string) {
  return useQuery({
    queryKey: ["ticket", id],
    queryFn: async () => {
      return await apiClient.getTicketById(id);
    },
    enabled: !!id,
  });
}

export function useCreateTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: TicketCreateRequest) => {
      return await apiClient.createTicket(data);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      toast.success("Ticket created successfully", {
        description: `Ticket ${data.ticketNumber} has been created.`,
      });
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to create ticket"));
    },
  });
}

export function useTicketByToken(token: string | null) {
  return useQuery({
    queryKey: ["ticket-by-token", token],
    queryFn: async () => {
      if (!token) return null;
      return await apiClient.getTicketByToken(token);
    },
    enabled: !!token,
  });
}

export function useAddPublicComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      token,
      content,
      requesterEmail,
    }: {
      token: string;
      content: string;
      requesterEmail: string;
    }) => {
      return await apiClient.addPublicComment(token, {
        content,
        requesterEmail,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["ticket-by-token", variables.token],
      });
      toast.success("Comment added successfully");
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to add comment"));
    },
  });
}

export function useFormOptions() {
  const configQuery = useQuery({
    queryKey: ["form-options"],
    queryFn: async () => {
      return await apiClient.getAllConfig();
    },
  });

  return {
    departments: configQuery.data?.departments ?? [],
    ticketTypes: configQuery.data?.ticketTypes ?? [],
    stages: configQuery.data?.stages ?? [],
    isLoading: configQuery.isLoading,
    error: configQuery.error,
  };
}

export function useTicketStats() {
  return useQuery({
    queryKey: ["ticket-stats"],
    queryFn: async () => {
      return await apiClient.getTicketStats();
    },
  });
}

// Hook for getting user's assigned tickets
export function useMyTickets() {
  return useTickets({ assigneeId: "me" }); // Backend should handle 'me' as current user
}

// Hook for getting tickets by department
export function useTicketsByDepartment(departmentId: string) {
  return useTickets({ departmentId });
}

// Hook for getting tickets by priority
export function useTicketsByPriority(priority: string) {
  return useTickets({ priority });
}

// Hook for searching tickets
export function useSearchTickets(searchQuery: string) {
  return useTickets({ search: searchQuery });
}
